import React, { useState, useEffect, useRef } from 'react';
import { useFileMedia } from '@/hooks/useFileMedia';
import { showImage } from '@/utils/show-image';

interface MediaPreviewProps {
  fileId: string;
  fileName?: string;
  fileSize?: number;
  mediaType: 'image' | 'video' | 'audio' | 'file';
  className?: string;
  onDownloadUrl?: (url: string) => void;
}

/**
 * 媒体预览组件 - 负责获取下载链接并展示媒体内容
 */
const MediaPreview: React.FC<MediaPreviewProps> = ({
  fileId,
  fileName,
  fileSize,
  mediaType,
  className = '',
  onDownloadUrl
}) => {
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const { getDownloadUrl } = useFileMedia();
  const fetchedFileIdRef = useRef<string | null>(null); // 用于防止重复请求

  useEffect(() => {
    const fetchDownloadUrl = async () => {
      // 防止重复请求相同的 fileId
      if (fetchedFileIdRef.current === fileId) {
        return;
      }

      try {
        setIsLoading(true);
        setError('');
        fetchedFileIdRef.current = fileId;

        const result = await getDownloadUrl({
          media_id: fileId,
          context: "conversation"
        });

        if (result?.download_url) {
          setDownloadUrl(result.download_url);
          onDownloadUrl?.(result.download_url);
        } else {
          setError('获取下载链接失败');
        }
      } catch (err: any) {
        console.error('获取媒体下载链接失败:', err);
        setError(err.message || '获取下载链接失败');
        fetchedFileIdRef.current = null; // 重置以便重试
      } finally {
        setIsLoading(false);
      }
    };

    if (fileId && fileId !== fetchedFileIdRef.current) {
      fetchDownloadUrl();
    }
  }, [fileId]); // 只依赖 fileId

  // 格式化文件大小
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-4 bg-gray-100 rounded-lg ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-sm text-gray-600">加载中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-sm text-red-600">⚠️ {error}</p>
        {fileName && (
          <p className="text-xs text-gray-500 mt-1">{fileName}</p>
        )}
      </div>
    );
  }

  if (!downloadUrl) {
    return (
      <div className={`p-4 bg-gray-100 rounded-lg ${className}`}>
        <p className="text-sm text-gray-600">暂无可用的媒体内容</p>
        {fileName && (
          <p className="text-xs text-gray-500 mt-1">{fileName}</p>
        )}
      </div>
    );
  }

  // 根据媒体类型渲染不同内容
  switch (mediaType) {
    case 'image':
      return (
        <div className={className}>
          <img
            src={downloadUrl}
            alt={fileName || '图片'}
            className="max-w-full max-h-64 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => showImage(downloadUrl)}
            onError={(e) => {
              console.error('图片加载失败:', e);
              setError('图片加载失败');
            }}
          />
        </div>
      );

    case 'video':
      return (
        <div className={className}>
          <video
            src={downloadUrl}
            controls
            className="max-w-full max-h-64 rounded-lg"
            onError={(e) => {
              console.error('视频加载失败:', e);
              setError('视频加载失败');
            }}
          >
            您的浏览器不支持视频播放
          </video>
        </div>
      );

    case 'audio':
      return (
        <div className={`p-3 bg-gray-50 rounded-lg border ${className}`}>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM15.657 6.343a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-1.929 5.657 1 1 0 11-1.414-1.414A7.971 7.971 0 0017 12a7.971 7.971 0 00-1.343-4.243 1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <audio
                src={downloadUrl}
                controls
                className="w-full"
                preload="metadata"
                onError={(e) => {
                  console.error('音频加载失败:', e);
                  setError('音频加载失败');
                }}
              >
                <source src={downloadUrl} type="audio/mpeg" />
                <source src={downloadUrl} type="audio/wav" />
                <source src={downloadUrl} type="audio/ogg" />
                您的浏览器不支持音频播放。
              </audio>
            </div>
          </div>
        </div>
      );

    case 'file':
    default:
      return (
        <div className={`p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 cursor-pointer ${className}`}>
          <a
            href={downloadUrl}
            download={fileName}
            className="flex items-center space-x-3 text-blue-600 hover:text-blue-800"
          >
            <div className="flex-shrink-0">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {fileName || `文件_${fileId.slice(-8)}`}
              </p>
              {fileSize && (
                <p className="text-xs text-gray-500">{formatFileSize(fileSize)}</p>
              )}
            </div>
          </a>
        </div>
      );
  }
};

export default MediaPreview;
